import { useEffect, useState, type FormEvent } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';

const navItems = [
  { to: '/', label: '首页', end: true },
  { to: '/category/movie', label: '电影' },
  { to: '/category/series', label: '电视剧' },
  { to: '/category/short', label: '短视频' },
  { to: '/about', label: '关于我们' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [keyword, setKeyword] = useState('');
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const submitSearch = (e: FormEvent) => {
    e.preventDefault();
    const q = keyword.trim();
    navigate(q ? `/search?q=${encodeURIComponent(q)}` : '/search');
  };

  return (
    <header
      className={`fixed top-0 left-0 z-50 w-full transition-all duration-300 ${
        scrolled || menuOpen
          ? 'bg-background-950/95 backdrop-blur-md border-b border-background-200'
          : 'bg-gradient-to-b from-background-950/90 to-transparent'
      }`}
    >
      <div className="flex h-16 md:h-20 items-center gap-4 px-4 md:px-8">
        <Link to="/" className="flex items-center gap-2 shrink-0 cursor-pointer" aria-label="午夜视频 首页">
          <span className="w-9 h-9 md:w-10 md:h-10 flex items-center justify-center rounded-lg bg-primary-500 text-background-950">
            <i className="ri-moon-clear-fill text-xl md:text-2xl"></i>
          </span>
          <span className="font-heading text-lg md:text-xl font-bold tracking-tight text-foreground-950 whitespace-nowrap">
            午夜视频
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-1 ml-4">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                `px-4 py-2 rounded-md text-sm font-medium whitespace-nowrap transition-colors ${
                  isActive
                    ? 'text-primary-400 bg-background-200/60'
                    : 'text-foreground-700 hover:text-foreground-950 hover:bg-background-200/50'
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <form onSubmit={submitSearch} className="hidden md:flex items-center ml-auto">
          <div className="flex items-center gap-2 rounded-lg bg-background-200/70 border border-background-300 px-3 h-10 focus-within:border-primary-400 transition-colors">
            <i className="ri-search-line text-foreground-500 text-base"></i>
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索影视、题材、演员"
              className="w-40 lg:w-56 bg-transparent text-sm text-foreground-950 placeholder:text-foreground-500 outline-none"
              aria-label="搜索影视内容"
            />
          </div>
          <button
            type="submit"
            className="ml-2 h-10 px-4 rounded-lg bg-primary-500 text-background-950 text-sm font-semibold whitespace-nowrap cursor-pointer hover:bg-primary-400 transition-colors"
          >
            搜索
          </button>
        </form>

        <Link
          to="/search"
          className="md:hidden ml-auto w-10 h-10 flex items-center justify-center rounded-lg bg-background-200/70 text-foreground-700 cursor-pointer"
          aria-label="搜索"
        >
          <i className="ri-search-line text-lg"></i>
        </Link>

        <button
          type="button"
          onClick={() => setMenuOpen((v) => !v)}
          className="lg:hidden w-10 h-10 flex items-center justify-center rounded-lg bg-background-200/70 text-foreground-950 cursor-pointer"
          aria-label="打开菜单"
          aria-expanded={menuOpen}
        >
          <i className={menuOpen ? 'ri-close-line text-xl' : 'ri-menu-line text-xl'}></i>
        </button>
      </div>

      {menuOpen && (
        <div className="lg:hidden border-t border-background-200 bg-background-950/98 backdrop-blur-md px-4 py-4">
          <nav className="flex flex-col gap-1">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  `px-4 py-3 rounded-lg text-base font-medium transition-colors ${
                    isActive
                      ? 'text-primary-400 bg-background-200/70'
                      : 'text-foreground-700 hover:bg-background-200/50'
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>
          <form onSubmit={submitSearch} className="mt-3 flex items-center gap-2">
            <div className="flex-1 flex items-center gap-2 rounded-lg bg-background-200/70 border border-background-300 px-3 h-11">
              <i className="ri-search-line text-foreground-500"></i>
              <input
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索影视内容"
                className="flex-1 bg-transparent text-sm text-foreground-950 placeholder:text-foreground-500 outline-none"
                aria-label="搜索影视内容"
              />
            </div>
            <button
              type="submit"
              className="h-11 px-4 rounded-lg bg-primary-500 text-background-950 text-sm font-semibold whitespace-nowrap cursor-pointer"
            >
              搜索
            </button>
          </form>
        </div>
      )}
    </header>
  );
}